import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import healthApi from '@/api/healthApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Pill, Plus, Trash2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import NeedHelpButton from '@/components/NeedHelpButton';
import AppFooter from '@/components/AppFooter';

const Medications = () => {
  const navigate = useNavigate();
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    dosage: '',
    frequency: 'daily',
    times: [],
    reminder_enabled: true,
    notes: ''
  });

  useEffect(() => {
    fetchMedications();
  }, []);

  const fetchMedications = async () => {
    try {
      const { data } = await healthApi.getMedications();
      setMedications(data);
    } catch (error) {
      console.error('Error fetching medications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTime = () => {
    setFormData({ ...formData, times: [...formData.times, '09:00'] });
  };

  const handleTimeChange = (index, value) => {
    const newTimes = [...formData.times];
    newTimes[index] = value;
    setFormData({ ...formData, times: newTimes });
  };

  const handleRemoveTime = (index) => {
    setFormData({ ...formData, times: formData.times.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.dosage) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await healthApi.createMedication(formData);
      toast.success('Medication added successfully!');
      setDialogOpen(false);
      setFormData({ name: '', dosage: '', frequency: 'daily', times: [], reminder_enabled: true, notes: '' });
      fetchMedications();
    } catch (error) {
      toast.error('Failed to add medication');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this medication?')) {
      try {
        await healthApi.deleteMedication(id);
        toast.success('Medication deleted');
        fetchMedications();
      } catch (error) {
        toast.error('Failed to delete medication');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-healthloop-mint-50 to-teal-50 p-4" data-testid="medications-page">
      <div className="max-w-4xl mx-auto pt-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-4"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>

        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Medications</h1>
            <p className="text-gray-600">Manage your medication schedule</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-medication-button">
                <Plus className="h-4 w-4 mr-2" />
                Add Medication
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Medication</DialogTitle>
                <DialogDescription>Enter your medication details</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Medication Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Aspirin"
                    required
                    data-testid="med-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dosage">Dosage *</Label>
                  <Input
                    id="dosage"
                    value={formData.dosage}
                    onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                    placeholder="e.g., 500mg"
                    required
                    data-testid="med-dosage-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="frequency">Frequency</Label>
                  <Select value={formData.frequency} onValueChange={(value) => setFormData({ ...formData, frequency: value })}>
                    <SelectTrigger data-testid="med-frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="twice_daily">Twice Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="as_needed">As Needed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Reminder Times</Label>
                  {formData.times.map((time, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        type="time"
                        value={time}
                        onChange={(e) => handleTimeChange(index, e.target.value)}
                        data-testid={`med-time-${index}`}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemoveTime(index)}
                        data-testid={`remove-time-${index}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" onClick={handleAddTime} className="w-full" data-testid="add-time-button">
                    <Clock className="h-4 w-4 mr-2" />
                    Add Time
                  </Button>
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="reminder">Enable Reminders</Label>
                  <Switch
                    id="reminder"
                    checked={formData.reminder_enabled}
                    onCheckedChange={(checked) => setFormData({ ...formData, reminder_enabled: checked })}
                    data-testid="reminder-switch"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Any special instructions?"
                    rows={3}
                    data-testid="med-notes-textarea"
                  />
                </div>

                <Button type="submit" className="w-full" data-testid="submit-medication">
                  Add Medication
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-healthloop-mint-600"></div>
          </div>
        ) : medications.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Pill className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">No medications added yet</p>
              <Button onClick={() => setDialogOpen(true)}>Add Your First Medication</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4" data-testid="medications-list">
            {medications.map((med) => (
              <Card key={med.id}>
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <Pill className="h-5 w-5 text-red-600 mr-2" />
                        <h3 className="text-xl font-semibold">{med.name}</h3>
                      </div>
                      <p className="text-gray-600 mb-2">Dosage: {med.dosage}</p>
                      <p className="text-sm text-gray-500 mb-2">Frequency: {med.frequency.replace('_', ' ')}</p>
                      {med.times && med.times.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {med.times.map((time, idx) => (
                            <span key={idx} className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                              <Clock className="h-3 w-3 mr-1" />
                              {time}
                            </span>
                          ))}
                        </div>
                      )}
                      {med.notes && (
                        <p className="text-sm text-gray-600 mt-2">
                          <span className="font-semibold">Notes:</span> {med.notes}
                        </p>
                      )}
                      {med.reminder_enabled && (
                        <span className="inline-block mt-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                          🔔 Reminders ON
                        </span>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(med.id)}
                      className="text-red-600 hover:text-red-700"
                      data-testid={`delete-med-${med.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      <NeedHelpButton />
      <AppFooter />
    </div>
  );
};

export default Medications;
